import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { places } from '../../mocks/places';

interface MapSidebarProps {
  places: (typeof places[0] & { distance?: number | null })[];
  isOpen: boolean;
  onToggle: () => void;
  sortBy: 'rating' | 'distance' | 'popularity' | 'price';
  onSortChange: (sort: 'rating' | 'distance' | 'popularity' | 'price') => void;
  onPlaceSelect: (place: typeof places[0] | null) => void;
  selectedPlace: typeof places[0] | null;
  userLocation: { lat: number; lng: number } | null;
}

export default function MapSidebar({
  places: filteredPlaces,
  isOpen,
  onToggle,
  sortBy,
  onSortChange,
  onPlaceSelect,
  selectedPlace,
  userLocation,
}: MapSidebarProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [listCollapsed, setListCollapsed] = useState(false);

  const formatDistance = (distance: number | null | undefined): string => {
    if (distance === null || distance === undefined) return '';
    return distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`;
  };

  const getPriceSymbol = (budget: number): string => '$'.repeat(budget);

  const handlePlaceClick = (place: typeof places[0]) => {
    onPlaceSelect(place);
    window.dispatchEvent(new CustomEvent('sidebar-place-click', { detail: { place } }));
  };

  const handlePlaceHover = (placeId: number | null) => {
    window.dispatchEvent(new CustomEvent('sidebar-place-hover', { detail: { placeId } }));
  };

  const SORT_OPTIONS: { key: 'rating' | 'distance' | 'popularity' | 'price'; icon: string; labelEn: string; labelMn: string }[] = [
    { key: 'rating',     icon: 'ri-star-line',                labelEn: 'Rating',  labelMn: 'Үнэлгээ'  },
    { key: 'distance',   icon: 'ri-map-pin-line',             labelEn: 'Distance',labelMn: 'Зай'       },
    { key: 'popularity', icon: 'ri-fire-line',                labelEn: 'Popular', labelMn: 'Алдартай' },
    { key: 'price',      icon: 'ri-money-dollar-circle-line', labelEn: 'Price',   labelMn: 'Үнэ'       },
  ];

  /* ── Toggle button — floats over the map, positioned below the filters bar ── */
  const toggleBtn = (
    <button
      onClick={onToggle}
      style={{ top: 'calc(64px + var(--filters-bar-height, 120px) + 12px)' }}
      className="fixed left-4 z-40 bg-white rounded-full shadow-lg p-2.5 hover:bg-gray-50 transition-all duration-300 hover:scale-110 border border-gray-100"
      aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
    >
      <div className="w-5 h-5 flex items-center justify-center">
        <i className={`ri-${isOpen ? 'arrow-left' : 'arrow-right'}-s-line text-lg text-gray-700`} />
      </div>
    </button>
  );

  /* ── Sidebar panel ── */
  const sidebarTop = 'calc(64px + var(--filters-bar-height, 120px))';
  const sidebarHeight = 'calc(100vh - 64px - var(--filters-bar-height, 120px))';

  return (
    <>
      {toggleBtn}

      <aside
        style={{ top: sidebarTop, height: sidebarHeight }}
        className={`fixed lg:sticky lg:top-0 lg:h-full left-0 bg-white shadow-xl z-30 transition-all duration-300 flex flex-col overflow-hidden ${
          isOpen ? 'w-72 md:w-80' : 'w-0'
        }`}
      >
        {/* ── Header: title + count + collapse toggle ── */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900">
              {isEnglish ? 'Places' : 'Газрууд'}
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {filteredPlaces.length} {isEnglish ? 'found' : 'олдсон'}
              </span>
              {/* Collapse / expand list */}
              <button
                onClick={() => setListCollapsed(v => !v)}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                title={listCollapsed ? (isEnglish ? 'Expand list' : 'Жагсаалт нээх') : (isEnglish ? 'Collapse list' : 'Жагсаалт хаах')}
              >
                <i className={`ri-${listCollapsed ? 'arrow-down' : 'arrow-up'}-s-line text-sm text-gray-600`} />
              </button>
            </div>
          </div>

          {/* Sort tags — wrap, no horizontal scroll */}
          <div className="flex flex-wrap gap-1.5">
            {SORT_OPTIONS.map(opt => {
              const isDisabled = opt.key === 'distance' && !userLocation;
              const isActive = sortBy === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => !isDisabled && onSortChange(opt.key)}
                  disabled={isDisabled}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? 'bg-[#0066CC] text-white shadow-sm'
                      : isDisabled
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'
                  }`}
                >
                  <i className={`${opt.icon} text-xs`} />
                  {isEnglish ? opt.labelEn : opt.labelMn}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Places list (collapsible) ── */}
        <div
          className={`flex-1 overflow-y-auto transition-all duration-300 ${
            listCollapsed ? 'max-h-0 overflow-hidden' : 'max-h-full'
          }`}
        >
          <div className="p-3 space-y-2">
            {filteredPlaces.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-map-pin-line text-4xl text-gray-300" />
                </div>
                <p className="text-gray-500 text-sm">{isEnglish ? 'No places found' : 'Газар олдсонгүй'}</p>
                <p className="text-gray-400 text-xs mt-1">{isEnglish ? 'Try adjusting your filters' : 'Шүүлтүүрээ өөрчилж үзнэ үү'}</p>
              </div>
            ) : (
              filteredPlaces.map((place, index) => (
                <div
                  key={place.id}
                  onClick={() => handlePlaceClick(place)}
                  onMouseEnter={() => handlePlaceHover(place.id)}
                  onMouseLeave={() => handlePlaceHover(null)}
                  className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border-2 ${
                    selectedPlace?.id === place.id
                      ? 'border-[#0066CC] scale-[1.01]'
                      : 'border-transparent hover:border-gray-200'
                  }`}
                  style={{ animation: `fadeInUp 0.25s ease-out ${index * 0.04}s both` }}
                >
                  <div className="flex gap-2.5 p-2.5">
                    {/* Thumbnail */}
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={place.image}
                        alt={isEnglish ? place.nameEn : place.nameMn}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1 mb-0.5">
                        <h3 className="font-semibold text-xs text-gray-900 truncate leading-tight">
                          {isEnglish ? place.nameEn : place.nameMn}
                        </h3>
                        {place.isOpen && (
                          <span className="flex items-center gap-0.5 text-[10px] text-green-600 whitespace-nowrap flex-shrink-0">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                            {isEnglish ? 'Open' : 'Нээлттэй'}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 mb-0.5">
                        <div className="flex items-center gap-0.5">
                          <i className="ri-star-fill text-[#FFD700] text-[10px]" />
                          <span className="text-[11px] font-semibold text-gray-700">{place.rating}</span>
                        </div>
                        <span className="text-gray-300 text-[10px]">•</span>
                        <span className="text-[11px] font-medium text-gray-500">{getPriceSymbol(place.budget)}</span>
                      </div>

                      <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                        <span className="truncate">{place.category}</span>
                        {place.distance !== null && place.distance !== undefined && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="flex items-center gap-0.5 text-[#0066CC] font-medium whitespace-nowrap">
                              <i className="ri-map-pin-line text-[10px]" />
                              {formatDistance(place.distance)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Collapsed state hint */}
        {listCollapsed && (
          <div
            className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400 cursor-pointer hover:text-gray-600 transition-colors border-t border-gray-100"
            onClick={() => setListCollapsed(false)}
          >
            <i className="ri-arrow-down-s-line text-sm" />
            <span>{isEnglish ? `Show ${filteredPlaces.length} places` : `${filteredPlaces.length} газар харах`}</span>
          </div>
        )}
      </aside>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </>
  );
}
